import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { NavigationDrawer } from "@/components/NavigationDrawer";
import {
  Shield,
  Mail,
  CheckCircle2,
} from "lucide-react";
import logoImage from "@/assets/logo.png";
import bgImage from "@/assets/bg.jpg";
import MembershipCard from "@/components/MemberCard";
import { MemberCardEmpty } from "@/components/MemberCardEmpty";
import api from "@/lib/api";
import { getMemberCard } from "@/services";
import { getFormattedDate } from "@/lib/date-manipulation";
import MemberCardCarousel from "@/components/MemberCardCarousel";

const Index = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    const memberId = searchParams.get("id");
    if (memberId) {
      // Jika ada ID anggota di URL, ambil data anggota
      getMemberCard(memberId)
        .then((response) => {
          setData(response.data?.data);
        })
        .catch((err) => {
          console.error("Error fetching member data:", err);
          setData(null);
        });
    } 
  }, []);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-union-red/5"></div>
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${bgImage})` }}
      ></div>

      {/* Main Content */}
      <div className="relative min-h-screen flex flex-col">
        {/* Header - Reduced padding for mobile */}
        <header className="absolute top-0 left-0 right-0 z-10 py-3 md:p-6">
          <div className="container mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img
                  src={logoImage}
                  alt="Logo"
                  className="h-8 w-8 h-auto object-cover"
                />
                <span className="text-xl font-bold text-foreground">
                  FSP LEM SPSI
                </span>
              </div>
              <nav className="hidden md:flex items-center gap-6">
                <NavigationDrawer title="Tentang Kami" type="about">
                  <Button
                    variant="ghost"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Tentang
                  </Button>
                </NavigationDrawer>
                <NavigationDrawer title="Kontak" type="contact">
                  <Button
                    variant="ghost"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Kontak
                  </Button>
                </NavigationDrawer>
              </nav>
              {/* Responsive menu untuk mobile */}
              <nav className="flex md:hidden items-center gap-2">
                <NavigationDrawer title="Tentang Kami" type="about">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Tentang"
                  >
                    <span className="sr-only">Tentang</span>
                    <Shield className="h-5 w-5" />
                  </Button>
                </NavigationDrawer>
                <NavigationDrawer title="Kontak" type="contact">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Kontak"
                  >
                    <span className="sr-only">Kontak</span>
                    <Mail className="h-5 w-5" />
                  </Button>
                </NavigationDrawer>
              </nav>
            </div>
          </div>
        </header>

        {/* Hero Section - Reduced padding for mobile */}
        <main className="flex-1 flex items-center justify-center md:px-6 py-24">
          <div className="container mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Left Content */}
              <div className="space-y-6 md:space-y-8 text-center lg:text-left">
                <div className="space-y-4 md:space-y-6 text-center lg:text-left">
                  <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold leading-tight">
                    <span className="text-foreground">
                      {data ? (
                        <>
                          <span className="flex w-full m-auto justify-center items-center md:justify-start md:items-start"><CheckCircle2 className="w-20 h-20 mb-2 text-green-500" /></span>
                          <span className="capitalize">{data.name}</span> adalah {(
                            data.boards && data.boards.length > 0 
                              ? (data.boards.some(board => board.role_name) ? "Pengurus" : "Anggota")
                              : (data?.role_name ? "Pengurus" : "Anggota")
                          )} 
                        </>
                      ) : (
                        'FSP LEM SPSI'
                      )}
                    </span>
                    <br />
                    <span className="bg-gradient-union bg-clip-text text-transparent">
                      {data ? 
                        (() => {
                          switch(data?.status) {
                            case 'active':
                              return "Aktif";
                            case 'inactive':
                              return "Non Aktif";
                            case 'pending':
                              return "Pending";
                            case 'rejected':
                              return "Ditolak";
                            default:
                              return "Non Aktif";
                          }
                        })() : 
                        "Digital"
                      }
                    </span>
                  </h1>
                  <p className="text-lg md:text-xl lg:text-2xl text-muted-foreground max-w-2xl">
                    {data ? 
                      (data?.role_name ? 
                        <>
                          Berlaku sampai <strong>{getFormattedDate(data?.expired_at, "MMM y")}</strong>
                        </> : 
                        <>
                          Bergabung sejak <strong>{getFormattedDate(data?.joined_at, "MMM y")}</strong> 
                        </>
                      ) : 
                      "Aplikasi Validasi Keanggotaan Digital FSP LEM SPSI"
                    }
                  </p>
                </div>

                {/* Stats - Reduced gap for mobile */}
                {!data ? 
                <div className="grid grid-cols-3 gap-4 md:gap-8 pt-6 md:pt-8">
                  <div className="text-center lg:text-left">
                    <div className="text-2xl md:text-3xl font-bold text-primary">200K+</div>
                    <div className="text-xs md:text-sm text-muted-foreground">
                      Anggota Aktif
                    </div>
                  </div>
                  <div className="text-center lg:text-left">
                    <div className="text-2xl md:text-3xl font-bold text-primary">500+</div>
                    <div className="text-xs md:text-sm text-muted-foreground">
                      Perusahaan
                    </div>
                  </div>
                  <div className="text-center lg:text-left">
                    <div className="text-2xl md:text-3xl font-bold text-primary">12</div>
                    <div className="text-xs md:text-sm text-muted-foreground">
                      Provinsi
                    </div>
                  </div>
                </div>
                : null}
              </div>

              {/* Right Content - Member Card */}
              <div className="relative">
                <div className="relative z-10">
                  {data ? (
                    data.boards && data.boards.length > 0 ? (
                      <MemberCardCarousel data={data} />
                    ) : (
                      <MembershipCard data={data} />
                    )
                  ) : (
                    <MemberCardEmpty />
                  )}
                </div>

                {/* Decorative Elements */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-union rounded-full opacity-20 animate-pulse"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-solidarity rounded-full opacity-30 animate-pulse delay-1000"></div>
              </div>
            </div>
          </div>
        </main>

        {/* Footer - Reduced padding for mobile */}
        <footer className="relative z-10 border-t bg-card/50 backdrop-blur-sm">
          <div className="container mx-auto px-3 md:px-6 py-6 md:py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3"></div>
              <div className="flex items-center gap-6 text-xs md:text-sm text-muted-foreground">
                <span>
                  All right reserved © {new Date().getFullYear()} FSP LEM SPSI
                </span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Index;
